/**
 * Golem fork (FT-1403 phase 1 / FT-1397 items 1-3): THE NIGHT SIGNS
 * REGISTRY — one per-role entry for everything the night dress used to
 * decide by scattered role-id lists and guards.
 *
 * THE SHAPE. A role's night signs are three kinds of fact, and the registry
 * splits them by who can read them:
 *
 *   INK    the role's primary colour — style-readable, so it lives in the
 *          SCSS half of this registry, `golem/_roleInk.scss` (NightMark's
 *          palettes and Player.vue's glow maps both derive from that one
 *          map; see its header for the drift hazard it kills).
 *
 *   FLAGS  which pieces of dress furniture a role wants — JS-readable,
 *          here. These replace the hand-kept lists and role-id guards the
 *          audit named: NightMark's HAS_ART and its three told guards
 *          (character token / numeral badge / ledger), NightCall's chip and
 *          book branches, NightThread's line-token branch. A surface asks
 *          `signFlag(roleId, "wantsX")` and never names a role again.
 *
 *   RULES  per-role behavior keys that are neither art nor copy — today the
 *          one automation-suppression key (the Scarlet Woman's checklist
 *          row stands down while her automation covers it, FT-1314), read
 *          generically by the night roster instead of a hard-coded id.
 *
 * WHAT STAYS WHERE IT IS. TOLD_ROLES (the telling's data shape) keeps
 * living in golem/toldInfo — that is the information's grammar, not its
 * dress. TOLD_ART also stays authored there (the role-by-role landing
 * order is that file's story); this registry re-reads it so every surface
 * asks ONE module which dress exists. The 12-branch render itself stays in
 * NightMark.vue — the module split is a later card (the audit's item 4),
 * deliberately not this one.
 *
 * ── COPY AS DATA (FT-1403 phase 1, the override layer) ────────────────────
 * The per-role night COPY stays authored where it always was — NIGHT_INFO's
 * `line`, CHOOSER_VERBS' invite/sealed, toldInfo's sentence builders — as
 * the DEFAULTS. What is new is that every read goes through `copyFor()`
 * below, which consults a per-script OVERRIDE first: a custom script may
 * carry a `nightCopy` section in its `_meta` pseudo-role,
 *
 *   "nightCopy": {
 *     "<roleId>": {
 *       "line":    "the storyteller checklist row's instruction sentence",
 *       "invite":  "the chooser ask's own sentence ({names} filled live)",
 *       "sealed":  "the sealed receipt ({names})",
 *       "telling": "a told role's bright sentence ({names}/{number}/{character})",
 *       "settled": "its residue sentence (same placeholders)",
 *       "seats":   "the sheet row's label before the seat pickers (FT-1410)",
 *       "told":    "...and the one before the told control"
 *     }, ...
 *   }
 *
 * DELIVERY. parseRoles lifts a script's `_meta` whole onto the custom
 * edition object, so the section rides `state.edition` — which is exactly
 * what localStorage persists and what the socket's edition frame sends to
 * every client. store/index.js's setEdition is the ONE ingest door: it
 * sanitizes the section (registerNightCopy below) on every landing — local
 * load, reload, vault load, the wire — and an official edition clears it.
 *
 * SANITIZED HARD, both because a script is shared content and because the
 * wire replays it: strings only, markup stripped, the schema's own
 * 200-char cap, role ids cleaned to the app's id alphabet, at most 100
 * roles. A script WITHOUT the section changes nothing anywhere.
 *
 * A chooser's verb pair only stands when BOTH sentences resolve (override
 * or default): NightCall's whole verb grammar (chips yielding to named
 * lines, the padded Confirm) assumes a complete pair, so an undressed role
 * overriding only `invite` keeps today's generic lines until it authors
 * `sealed` too.
 *
 * REACTIVE BY CONSTRUCTION (FT-1403 phase 2). The override map is not a
 * plain module variable: it hangs off a `Vue.observable`, so `copyFor`'s
 * read of it IS a dependency for every computed and store getter that
 * resolves a night sentence. Swapping the map (registerNightCopy) therefore
 * repaints the face, the told sentence and the storyteller's sheet with no
 * reload — which is exactly what the Night lab's live copy editor needs,
 * and which costs the ordinary script-landing path nothing.
 */
import Vue from "vue";
import { TOLD_ART, toldSentence } from "./toldInfo";
import { chooserVerbsFor, lineFor, splitLabelsFor } from "./nightInfo";
// FT-1404: the viewer's own "Show night signs" switch — a personal pref
// (golem/prefs' showNightSigns), mirrored below into one reactive gate.
import { prefsState, PREFS_EVENT } from "./prefs";

/**
 * The CHOOSING roles whose night-mark art has landed — grows one commit at
 * a time (FT-1384's role-by-role order: monk, poisoner, fortuneteller,
 * butler, imp, ravenkeeper). Everything else renders nothing and keeps the
 * app's standing purple idiom. (Moved whole from NightMark.vue, where it
 * was the template's HAS_ART.)
 */
const CHOOSER_ART = [
  "monk",
  "poisoner",
  "fortuneteller",
  "butler",
  "imp",
  "ravenkeeper",
];

/**
 * The per-role flag entries. A missing role, or a missing key, is FALSE —
 * the fallback is always "no extra furniture", which is exactly how an
 * undressed or unlisted role has always rendered.
 *
 *   wantsCharacterToken  the told character's icon hangs on the role's own
 *                        coin (the Librarian's bookmark token, FT-1400; the
 *                        Grandmother's spectacles token, FT-1402)
 *   wantsNumeralBadge    the told number persists as a badge on the coin
 *                        (the Empath's ring, the Chef's scorch — FT-1400)
 *                        and the number chip on the face wears the role's
 *                        own dress (NightCall's nf-num-* classes)
 *   wantsLedger          the nightly number history accumulates round the
 *                        coin (the Empath's FT-1401 ledger), and the face's
 *                        number chips speak its glyphs (heart for 0, the
 *                        blood run for a count)
 *   wantsLineToken       the told character's icon rides the thread's
 *                        midpoint (the Washerwoman's laundry line, the
 *                        Investigator's evidence string — FT-1400; the
 *                        Librarian's token hangs on her bookmark instead)
 *   wantsBook            the Librarian's book renders under her sentence on
 *                        the face, both beats (FT-1385)
 *   automationSuppress   the session automation flag that, while ON, stands
 *                        this role's checklist row down entirely (FT-1314's
 *                        user ruling — the automation does the row's whole
 *                        job, reveal included)
 */
const SIGNS = {
  washerwoman: { wantsLineToken: true },
  librarian: { wantsCharacterToken: true, wantsBook: true },
  investigator: { wantsLineToken: true },
  chef: { wantsNumeralBadge: true },
  empath: { wantsNumeralBadge: true, wantsLedger: true },
  grandmother: { wantsCharacterToken: true },
  // FT-1412: the Undertaker — the executed character's token on his own
  // coin (the told-character pattern's third member, no seat)
  undertaker: { wantsCharacterToken: true },
  scarletwoman: { automationSuppress: "autoScarletWoman" },
};

/** Has this CHOOSING role's mark art landed? (NightMark's old HAS_ART.) */
export function hasChooserArt(roleId) {
  return CHOOSER_ART.includes(roleId);
}

/** Has this TOLD role's two-beat dress landed? (toldInfo's TOLD_ART,
 *  re-read here so the surfaces ask one module.) */
export function hasToldArt(roleId) {
  return TOLD_ART.includes(roleId);
}

/** One dress-furniture flag, false for every role and key not listed. */
export function signFlag(roleId, flag) {
  const entry = SIGNS[roleId];
  return !!(entry && entry[flag]);
}

/** The session-automation flag that suppresses this role's checklist row
 *  while ON, or "" — the night roster reads this instead of a role id. */
export function automationSuppressFor(roleId) {
  const entry = SIGNS[roleId];
  return (entry && entry.automationSuppress) || "";
}

// ─── THE COPY OVERRIDE LAYER (see COPY AS DATA in the header) ──────────────

/** The keys a script may override, and the one cap they all share — the
 *  same 200 characters sanitizeAuthoredNight already allows a line. The cap
 *  is exported because the lab's editor boxes enforce the same number in the
 *  markup rather than inventing a second one. */
export const COPY_KEYS = [
  "line",
  "invite",
  "sealed",
  "telling",
  "settled",
  // FT-1410: the SPLIT ROW's two labels on the storyteller's night sheet —
  // the word before the seat pickers and the word before the told control
  // ("Player:" … "Role:" on the first-night info rows). Two more keys through
  // the same door: the sanitizer loops COPY_KEYS, so a script, the Night lab
  // and the forge all carry them for free.
  "seats",
  "told",
];
export const COPY_MAX = 200;
const COPY_MAX_ROLES = 100;

/**
 * The standing overrides — runtime-only, rebuilt wholesale on every
 * setEdition (the AUTHORED_NIGHT idiom: never persisted here; the script
 * itself is the persistent copy). `map` is roleId → { key: sentence }.
 *
 * ON AN OBSERVABLE, not a bare `let`: see REACTIVE BY CONSTRUCTION in the
 * header. Every reader below goes through `copyGate.map`, so the swap
 * notifies; nothing else in the app has to know the map exists.
 */
const copyGate = Vue.observable({ map: Object.create(null) });

/** One override sentence, made safe: a string, markup stripped (tags out
 *  whole, stray angle brackets too), whitespace collapsed, capped. */
function cleanCopyString(value) {
  if (typeof value !== "string") return "";
  return value
    .replace(/<[^>]*>/g, " ")
    .replace(/[<>]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, COPY_MAX);
}

/** The app's one role-id alphabet (store/index.js's clean). */
function cleanRoleId(id) {
  return String(id)
    .toLocaleLowerCase()
    .replace(/[^a-z0-9]/g, "");
}

/**
 * A raw `nightCopy` section → the sanitized override map, or null when
 * nothing survives. Strict on purpose — this is the one door PERSISTED and
 * WIRE data reaches the copy layer through (sanitizeAuthoredNight's rule).
 */
export function sanitizeNightCopy(raw) {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const out = {};
  let kept = 0;
  for (const rawId of Object.keys(raw)) {
    if (kept >= COPY_MAX_ROLES) break;
    const roleId = cleanRoleId(rawId);
    const entry = raw[rawId];
    if (!roleId || !entry || typeof entry !== "object" || Array.isArray(entry))
      continue;
    const clean = {};
    COPY_KEYS.forEach((key) => {
      const v = cleanCopyString(entry[key]);
      if (v) clean[key] = v;
    });
    if (Object.keys(clean).length) {
      out[roleId] = clean;
      kept += 1;
    }
  }
  return Object.keys(out).length ? out : null;
}

/** Replace the standing overrides — setEdition's call, every landing.
 *  Returns the sanitized map (for the store to keep on the edition), or
 *  null when the section was absent or nothing survived. */
export function registerNightCopy(raw) {
  const clean = sanitizeNightCopy(raw);
  copyGate.map = clean || Object.create(null);
  return clean;
}

/** The standing sanitized overrides, or null — what travels beside the
 *  script (the edition frame reads state.edition, which setEdition keeps
 *  in step with this). */
export function nightCopyOverrides() {
  const map = copyGate.map;
  return Object.keys(map).length ? map : null;
}

/**
 * THE accessor — one role's copy for one key, override first, the shipped
 * default second. `ctx.isFirstNight` feeds the `line` default (a shipped
 * line may be split { first, other }); the told sentence keys have no raw
 * default template (toldSentence composes those) so they answer "" and
 * `toldSentenceFor` below owns the fallback.
 */
export function copyFor(roleId, key, ctx) {
  const entry = copyGate.map[roleId];
  if (entry && entry[key]) return entry[key];
  return defaultCopyFor(roleId, key, ctx);
}

/**
 * THE OVERRIDE ALONE — the authored sentence for one role and key, or "".
 *
 * FT-1410: for the callers whose SHIPPED default is not this layer's to
 * decide. The night sheet's single-label row is the case: a row with one half
 * keeps its own verb ("Kills:", "Poisons:", "Dies:") — the user's ruling — so
 * the sheet asks for an override and falls back to the role's authored label
 * itself. Nothing changes for a script that says nothing, which is the whole
 * point of asking this way rather than moving the default in here.
 */
export function overrideFor(roleId, key) {
  const entry = copyGate.map[roleId];
  return (entry && entry[key]) || "";
}

/**
 * THE SHIPPED DEFAULT for one role and key, overrides ignored — what an
 * empty override box is standing in for. Split out of `copyFor` (rather
 * than restated anywhere) so the FT-1403 phase 2 editor can show the real
 * default as its placeholder without a second copy of a single string.
 */
export function defaultCopyFor(roleId, key, ctx) {
  switch (key) {
    case "line":
      return lineFor(roleId, ctx && ctx.isFirstNight);
    case "invite":
    case "sealed": {
      const verbs = chooserVerbsFor(roleId);
      return (verbs && verbs[key]) || "";
    }
    // FT-1410: the split row's two labels — STRUCTURAL, off the field list
    // (nightInfo's splitLabelsFor), never a role-name list here.
    case "seats":
    case "told":
      return splitLabelsFor(roleId)[key];
    default:
      return "";
  }
}

/** A chooser's verb templates through the override layer — the pair only
 *  stands complete (see the header's rule), null keeps the generic lines
 *  exactly as chooserVerbsFor's null always has. */
export function verbsFor(roleId) {
  const invite = copyFor(roleId, "invite");
  const sealed = copyFor(roleId, "sealed");
  return invite && sealed ? { invite, sealed } : null;
}

// ─── THE NIGHT SIGNS TOGGLE (FT-1404, user-decided) ────────────────────────
// One gate, consulted by the DRESS layer alone: NightMark's art, the
// night threads, the coin glows (Player.vue's night-role-/told- classes),
// the Empath's ledger. Everything FUNCTIONAL ignores it — picks, staging,
// Confirm, the verb copy, the answer chips, the generic purple pick ring —
// so signs OFF plays exactly like an undressed role always has. (The
// FT-1396 evil chair badges are team knowledge, not night signs, and never
// consult this.)
//
// prefsState is a plain module object (its own header's rule), so the gate
// keeps a Vue.observable mirror refreshed on PREFS_EVENT — a computed that
// calls enabled() re-runs the moment the toggle flips, with no per-file
// readPrefs plumbing.
const signsGate = Vue.observable({
  on: prefsState.showNightSigns !== false,
});
try {
  window.addEventListener(PREFS_EVENT, () => {
    signsGate.on = prefsState.showNightSigns !== false;
  });
} catch (e) {
  // no window (tests): the boot value stands
}

/** Does this viewer's dress layer render the night signs? */
export function enabled() {
  return signsGate.on;
}

/** A telling's sentence through the override layer — the script's own
 *  template ({names} / {number} / {character}) when one is authored for
 *  this beat, toldInfo's composed sentence otherwise. */
export function toldSentenceFor(told, names, settled) {
  const tpl = told && copyFor(told.roleId, settled ? "settled" : "telling");
  if (tpl) {
    const number =
      told.number === null || told.number === undefined || told.number === ""
        ? ""
        : String(told.number);
    return tpl
      .replace(/\{names\}/g, names.filter(Boolean).join(" & "))
      .replace(/\{number\}/g, number)
      .replace(/\{character\}/g, told.characterName || "");
  }
  return toldSentence(told, names, settled);
}
